import { css } from "styled-components";

import { controlFocusRing, controlTransition } from "./controlStyles";

/**
 * control-fill 인터랙티브 컨트롤의 한 "상태"가 가지는 시각 3-슬롯. 면/테두리/전경.
 * 테두리 없는 외관은 `border: "transparent"`, 색을 상속받는 전경은 `foreground: "inherit"`.
 */
export interface SurfaceLayer {
  background: string;
  border: string;
  foreground: string;
}

/**
 * control-fill 인터랙티브 컨트롤(IconButton, clickable LabelChip 등)의 **상태 머신 스키마**.
 *
 * 어떤 상태가 존재하고 어떻게 진행하는지를 *타입으로 강제* 한다 — resolver 가 네 상태를 모두
 * 채워야 컴파일되므로 `:active`·disabled 누락 같은 드리프트가 원천 차단된다. 색 *값* 은
 * 컴포넌트별 팔레트(테마 tone vs 인스턴스 rgba)라 강제 대상이 아니지만, *상태 슬롯과 진행 구조*
 * 는 모든 소비자가 공유한다.
 *
 * selected(토글)는 별도 슬롯이 아니라 **resolver 입력** 으로 둔다 — 토글형 컨트롤은 resolver 가
 * 선택 상태에 맞는 네 상태 spec 을 반환한다(스키마를 최소로 유지).
 */
export interface InteractiveSurfaceSpec {
  rest: SurfaceLayer;
  hover: SurfaceLayer;
  active: SurfaceLayer;
  disabled: SurfaceLayer;
}

const layerCss = (layer: SurfaceLayer) => css`
  background-color: ${layer.background};
  border-color: ${layer.border};
  color: ${layer.foreground};
`;

/**
 * spec 의 상태 슬롯을 실제 CSS 로 전개한다. 셀렉터 구조·transition 타이밍·focus ring·disabled
 * 게이팅을 **이 함수가 단독 소유** 해서 모든 소비자가 동일한 인터랙션 메커니즘을 갖도록 강제한다
 * (값만 컴포넌트별, 구조는 공유).
 *
 * 레이아웃(display/size/padding)·radius·border-width 는 소유하지 않는다 — 컴포넌트 정체성이라
 * 각 styled 컴포넌트가 따로 선언하고 이 css 를 합성한다. 네이티브 disabled 가 동작하도록 소비자는
 * `<button>` 처럼 `:disabled` 가 매칭되는 엘리먼트로 렌더해야 한다.
 */
export function interactiveSurfaceCss(spec: InteractiveSurfaceSpec) {
  return css`
    cursor: pointer;
    ${controlTransition("background-color", "border-color", "color")}
    ${layerCss(spec.rest)}

    &:not(:disabled):hover {
      ${layerCss(spec.hover)}
    }

    &:not(:disabled):active {
      ${layerCss(spec.active)}
    }

    &:focus-visible {
      ${controlFocusRing()}
    }

    &:disabled {
      ${layerCss(spec.disabled)}
      cursor: not-allowed;
    }
  `;
}
